import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/i18n/LanguageContext";
import CartDrawer from "@/components/CartDrawer";
import logoPink from "@/assets/logo-pink.webp";

interface NavbarProps {
  visible: boolean;
}

const Navbar = ({ visible }: NavbarProps) => {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();

  const navItems = [
    { label: t("nav.shop"), target: "top-products" },
    { label: t("nav.about"), target: "about" },
    { label: t("nav.contact"), target: "contact" },
  ];

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Close lang dropdown on outside click
  useEffect(() => {
    if (!langOpen) return;
    const close = () => setLangOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [langOpen]);

  const handleNavClick = (target: string) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  return (
    <>
      <div
        className="fixed top-6 left-1/2 z-50 transition-all duration-700 ease-out w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] max-w-4xl"
        style={{
          opacity: visible ? 1 : 0,
          transform: `translateX(-50%) translateY(${visible ? "0" : "-20px"})`,
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        <nav className="flex items-center gap-1 rounded-full bg-background/90 backdrop-blur-md shadow-[0_2px_24px_rgba(0,0,0,0.08)] px-4 sm:px-5 py-2.5">
          {/* Brand logo */}
          <Link to="/" onClick={() => setMenuOpen(false)}>
            <img
              src={logoPink}
              alt="Dr. Dee"
              className="h-6 sm:h-7 w-auto select-none px-1"
              draggable={false}
            />
          </Link>

          {/* Language selector */}
          <div className="relative -ml-1">
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen((o) => !o); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 hover:bg-[#E88CA4]/10 active:scale-[0.95]"
              style={{ color: "rgba(232,140,164,0.85)", fontFamily }}
            >
              <Globe size={16} strokeWidth={1.8} />
              <span>{lang === "en" ? "EN" : "عربي"}</span>
            </button>

            {langOpen && (
              <div
                className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-w-[90px] z-[80]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { setLang("en"); setLangOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors hover:bg-pink-50 ${lang === "en" ? "text-[#E88CA4]" : "text-gray-600"}`}
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  English
                </button>
                <button
                  onClick={() => { setLang("ar"); setLangOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors hover:bg-pink-50 ${lang === "ar" ? "text-[#E88CA4]" : "text-gray-600"}`}
                  style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}
                >
                  عربي
                </button>
              </div>
            )}
          </div>

          <div className="flex-1" />

          {/* Desktop nav links */}
          {!isMobile &&
            navItems.map(({ label, target }) => (
              <button
                key={target}
                onClick={() => handleNavClick(target)}
                className="group relative px-4 py-1.5 text-sm font-medium transition-colors duration-200 active:scale-[0.97]"
                style={{
                  fontFamily,
                  color: "rgba(232,140,164,0.85)",
                }}
              >
                {label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 rounded-full bg-[#E88CA4] transition-all duration-300 ease-out group-hover:w-[60%]" />
              </button>
            ))}

          {/* Desktop cart */}
          {!isMobile && (
            <div className="ml-1" style={{ color: "rgba(232,140,164,0.85)" }}>
              <CartDrawer />
            </div>
          )}

          {/* Mobile cart + burger */}
          {isMobile && (
            <>
              <div style={{ color: "rgba(232,140,164,0.85)" }}>
                <CartDrawer />
              </div>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="p-2 rounded-full transition-all duration-200 active:scale-[0.95]"
                style={{ color: "rgba(232,140,164,0.85)" }}
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Mobile sidebar overlay */}
      {isMobile && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300"
            style={{
              opacity: menuOpen ? 1 : 0,
              pointerEvents: menuOpen ? "auto" : "none",
            }}
            onClick={() => setMenuOpen(false)}
          />

          <div
            className="fixed top-0 right-0 z-[70] h-full w-[280px] bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col"
            style={{
              transform: menuOpen ? "translateX(0)" : "translateX(100%)",
            }}
          >
            <div className="flex items-center justify-between px-5 pt-6 pb-4">
              <Link to="/" onClick={() => setMenuOpen(false)}>
                <img src={logoPink} alt="Dr. Dee" className="h-6 w-auto" draggable={false} />
              </Link>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                style={{ color: "#E88CA4" }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="h-px bg-gray-100 mx-5" />

            <div className="flex flex-col gap-1 px-3 pt-4">
              {navItems.map(({ label, target }) => (
                <button
                  key={target}
                  onClick={() => handleNavClick(target)}
                  className="w-full text-left px-4 py-3.5 rounded-xl text-base font-semibold transition-all duration-200 hover:bg-pink-50 active:scale-[0.98]"
                  style={{ fontFamily, color: "#a8405c" }}
                >
                  {label}
                </button>
              ))}
              <Link
                to="/shop"
                onClick={() => setMenuOpen(false)}
                className="w-full text-left px-4 py-3.5 rounded-xl text-base font-semibold transition-all duration-200 hover:bg-pink-50 active:scale-[0.98]"
                style={{ fontFamily, color: "#a8405c" }}
              >
                {t("nav.browseAll")}
              </Link>
            </div>

            {/* Mobile language toggle */}
            <div className="px-5 pt-4">
              <div className="flex items-center gap-2 px-2">
                <Globe size={16} style={{ color: "#E88CA4" }} />
                <button
                  onClick={() => setLang(lang === "en" ? "ar" : "en")}
                  className="text-sm font-semibold px-3 py-1.5 rounded-full transition-colors hover:bg-pink-50"
                  style={{ fontFamily, color: "#a8405c" }}
                >
                  {lang === "en" ? "عربي" : "English"}
                </button>
              </div>
            </div>

            <div className="flex-1" />

            <div className="px-5 pb-6">
              <div className="h-px bg-gray-100 mb-4" />
              <p
                className="text-[10px] text-gray-400 text-center"
                style={{ fontFamily }}
              >
                {t("sidebar.copyright")}
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
